import React, { useState, useCallback } from 'react';
import { Col, Row, Spinner, Alert } from 'react-bootstrap';
import { PageHeader } from '../Public/PageHeader/PageHeader';
import { ReCaptcha } from './ReCaptcha';
import { config } from '../../Utils/config';

export const ReCaptchaWrapper = ({
                                   action,
                                   children,
                                   onVerificationComplete,
                                   onVerificationError,
                                   title = "Page Verification Required",
                                   loadingMessage = "Verifying you're human...",
                                   botMessage = "🛑 This page is for humans only",
                                   errorMessage = "Verification Error"
                                 }) => {
  const [verificationStatus, setVerificationStatus] = useState('loading'); // 'loading', 'verified', 'bot_detected', 'error'
  const [error, setError] = useState('');
  
  // Default handlers for data collection
  const defaultVerificationComplete = useCallback((result) => {
    //console.log(`[${action}] ReCaptcha verification completed:`, result);
  }, [action]);
  
  const defaultVerificationError = useCallback((errorMsg) => {
    //console.error(`[${action}] ReCaptcha verification error:`, errorMsg);
  }, [action]);
  
  const handleVerificationComplete = (result) => {
    // Always call completion handler (for data collection)
    (onVerificationComplete || defaultVerificationComplete)(result);
    
    if (config.recaptchaBlockingEnabled) {
      // Check score - typically scores below threshold are considered bots
      if (result && result.score >= config.recaptchaScoreThreshold) {
        setVerificationStatus('verified');
      } else {
        setVerificationStatus('bot_detected');
      }
    } else {
      // Data collection mode - always show content
      setVerificationStatus('verified');
    }
  };
  
  const handleVerificationError = (errorMsg) => {
    // Always call error handler (for data collection)
    (onVerificationError || defaultVerificationError)(errorMsg);
    
    if (config.recaptchaBlockingEnabled) {
      setError(errorMsg);
      setVerificationStatus('error');
    } else {
      // Data collection mode - always show content
      setVerificationStatus('verified');
    }
  };
  
  // If blocking is disabled, always show content
  if (!config.recaptchaBlockingEnabled) {
    return (
      <>
        {/* Hidden ReCaptcha component for auto-execution */}
        <div style={{ display: 'none' }}>
          <ReCaptcha
            onVerificationComplete={handleVerificationComplete}
            onVerificationError={handleVerificationError}
            action={action}
            autoExecute={true}
          />
        </div>
        {/* Always show content in data collection mode */}
        {children}
      </>
    );
  }
  
  // Show loading state while verifying (blocking mode)
  if (verificationStatus === 'loading') {
    return (
      <>
        <PageHeader title={title} />
        <Row className="justify-content-center text-center">
          <Col md={8} className="p-5">
            <div className="mb-4">
              <Spinner animation="border" role="status" className="me-2" />
              <span>{loadingMessage}</span>
            </div>
            {/* Hidden ReCaptcha component for auto-execution */}
            <div style={{ display: 'none' }}>
              <ReCaptcha
                onVerificationComplete={handleVerificationComplete}
                onVerificationError={handleVerificationError}
                action={action}
                autoExecute={true}
              />
            </div>
          </Col>
        </Row>
      </>
    );
  }
  
  // Show bot detected message (blocking mode)
  if (verificationStatus === 'bot_detected') {
    return (
      <>
        <PageHeader title={title} />
        <Row className="justify-content-center text-center">
          <Col md={8} className="p-5">
            <div className="mb-4">
              <h2 className="text-danger">{botMessage}</h2>
            </div>
          </Col>
        </Row>
      </>
    );
  }
  
  // Show error state (blocking mode)
  if (verificationStatus === 'error') {
    return (
      <>
        <PageHeader title={title} />
        <Row className="justify-content-center text-center">
          <Col md={8} className="p-5">
            <Alert variant="warning">
              <h4>{errorMessage}</h4>
              <p>{error}</p>
              <p>Please refresh page to try again.</p>
            </Alert>
          </Col>
        </Row>
      </>
    );
  }
  
  // Show content if verified (blocking mode)
  return <>{children}</>;
};

export default ReCaptchaWrapper;